<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import dayjs from 'dayjs'
    import { faBan, faClockRotateLeft, faDownload, faEnvelope, faPaperPlane, faSignature, faTrash, faUser } from '@fortawesome/free-solid-svg-icons'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import Popover from '../../_common/popover.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import FormV2SignLogPopover from './form-v2-sign-log-popover.svelte'
    import { SendMessageTemplate } from '../../_common/send-message.template'
    import type { Person } from '../../../modules/eventsApi/persons/person'
    import type { FormV2Form } from '../../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'
    import type { FormV2FormSignature } from '../../../modules/toolsApi/forms-v2/form-v2-form-signatures/form-v2-form-signature'

    const dispatch = createEventDispatcher()

    export let formV2FormId: string

    const COMPLETED = 100
    const SIGNING = 105
    const SIGNED = 110
    const MODE_LINK = 1
    const MODE_EMAIL = 2

    let showLog = false

    const modeOptions = [
        { label: 'Link de assinatura (assinatura simples)', value: MODE_LINK },
        { label: 'Assinatura por e-mail (assinatura avançada)', value: MODE_EMAIL },
    ]

    let show = false
    let starting = false
    let form: FormV2Form | null = null
    let signers: FormV2FormSignature[] = []
    let mode = MODE_LINK
    let newPerson: Person = null
    let resendingId: string | null = null
    let missingSignerInfo: { personId: string; personName: string; missingDocument: boolean; missingEmail: boolean }[] | null = null

    // Assinatura é decidida pelo modelo e congelada no snapshot na emissão. Os grids já escondem o
    // botão que abre este popover quando o modelo não pede assinatura; isto aqui cobre o caso de uma
    // listagem desatualizada e evita oferecer um "Iniciar assinatura" que o backend recusaria.
    function isSignatureRequested(target: FormV2Form | null) {
        return !!(target?.document as any)?.templateSnapshot?.headerFooter?.signature?.requested
    }
    $: signatureRequested = isSignatureRequested(form)

    onMount(loadAll)

    async function loadAll() {
        try {
            show = true
            form = await window.api.tools.formsV2.forms.getOne(formV2FormId)
            signers = await window.api.tools.formsV2.formSignatures.getAll({ formV2FormId })
            await ensureLinkedPersonSigner()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    // A pessoa vinculada ao documento (form.personId) já deve aparecer como assinante assim que o
    // popover abre, sem precisar ser adicionada manualmente.
    async function ensureLinkedPersonSigner() {
        if (form.status !== COMPLETED) return
        // Lê direto de `form`, não da variável reativa: esta função roda dentro do loadAll, antes de
        // o Svelte ter necessariamente recalculado os `$:`.
        if (!isSignatureRequested(form)) return
        if (!form.personId) return
        if (signers.some((s) => s.personId === form.personId)) return
        await window.api.tools.formsV2.formSignatures.create({
            formV2FormId,
            personId: form.personId,
            personName: form.personName,
        })
        signers = await window.api.tools.formsV2.formSignatures.getAll({ formV2FormId })
    }

    async function addSigner() {
        if (!newPerson?.personId) return
        try {
            show = true
            await window.api.tools.formsV2.formSignatures.create({
                formV2FormId,
                personId: newPerson.personId,
                personName: newPerson.name,
            })
            newPerson = null
            await loadAll()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function removeSigner(formV2FormSignatureId: string) {
        try {
            show = true
            await window.api.tools.formsV2.formSignatures.remove(formV2FormSignatureId)
            await loadAll()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function startSigning() {
        if (signers.length === 0) return
        try {
            starting = true
            missingSignerInfo = null
            await window.api.tools.formsV2.forms.startSigning(formV2FormId, mode)
            await loadAll()
            dispatch('refresh')
        } catch (error: any) {
            if (error?.code === 'MISSING_SIGNER_INFO') {
                missingSignerInfo = error.signers
            } else {
                window.error(error)
            }
        } finally {
            starting = false
        }
    }

    async function stopSigning() {
        if (!confirm('Interromper a assinatura? Os links enviados deixarão de funcionar.')) return
        try {
            show = true
            await window.api.tools.formsV2.forms.stopSigning(formV2FormId)
            await loadAll()
            dispatch('refresh')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function downloadPdf() {
        if (!form?.signedDocumentUrl) return
        window.open(form.signedDocumentUrl, '_blank')
    }

    function downloadReport() {
        if (!form?.signatureReportUrl) return
        window.open(form.signatureReportUrl, '_blank')
    }

    function signerStatusLabel(signer: FormV2FormSignature) {
        if (signer.signedAt) return 'Assinado'
        if (signer.viewedAt) return 'Visualizado'
        if (signer.emailSentAt) return 'E-mail enviado'
        return 'Pendente'
    }

    // id e token são uuid; vão em base58 porque o WhatsApp passou a tratar uuid dentro de link como
    // chave pix aleatória (mesmo formato) e renderizava só o uuid, não o link. Mesmo esquema já usado
    // nos links de preenchimento (ver compactToken em form-v2-form-link-popover.svelte). A
    // form-v2-sign-page desfaz e segue aceitando uuid cru, dos links já enviados.
    function signLinkQuery(signer: FormV2FormSignature) {
        // Sempre pelo objeto: `uuidToBase58` usa `this` internamente, e guardá-lo numa variável
        // solta desacopla o método da instância — em módulo ES o `this` vira undefined e a chamada
        // estoura em "Cannot read properties of undefined (reading 'hexToUint8Array')".
        const conversions = window.api.utils.conversions
        return (
            'public/forms-v2/assinar?id=' +
            conversions.uuidToBase58(signer.formV2FormSignatureId) +
            '&t=' +
            (signer.token ? conversions.uuidToBase58(signer.token) : '')
        )
    }

    function buildSignLink(signer: FormV2FormSignature) {
        return window.api.utils.utils.domain() + '/' + signLinkQuery(signer)
    }

    function sendSignLink(signer: FormV2FormSignature) {
        window.message(SendMessageTemplate.share_link_02(signLinkQuery(signer), 'Link de assinatura', signer.personId))
    }

    async function resendEmail(formV2FormSignatureId: string) {
        try {
            resendingId = formV2FormSignatureId
            await window.api.tools.formsV2.formSignatures.resendEmail(formV2FormSignatureId)
            await loadAll()
        } catch (error) {
            window.error(error)
        } finally {
            resendingId = null
        }
    }
</script>

<div class="root">
    <Zframe class="m-3">
        {#if form}
            {#if form.status === COMPLETED && !signatureRequested}
                <div class="alert alert-info mb-0">
                    Este modelo de documento não solicita assinatura. Para habilitá-la, ative "Solicitar assinatura ao preencher" nas propriedades do
                    modelo e emita um novo documento.
                </div>
            {:else if form.status === COMPLETED}
                {#if missingSignerInfo}
                    <div class="alert alert-warning">Não foi possível iniciar a assinatura: um ou mais assinantes estão com o cadastro incompleto.</div>
                    <div class="signers">
                        {#each missingSignerInfo as item (item.personId)}
                            <div class="signer_row">
                                <div class="signer_name">
                                    {item.personName}
                                    <div class="signer_missing">
                                        {#if item.missingDocument}<span>CPF/CNPJ não informado</span>{/if}
                                        {#if item.missingDocument && item.missingEmail}<span> · </span>{/if}
                                        {#if item.missingEmail}<span>E-mail não informado</span>{/if}
                                    </div>
                                </div>
                                <Zbutton icon={faUser} color="secondary" label="Pessoa" on:click={() => window.person('show', item.personId)} />
                            </div>
                        {/each}
                    </div>
                    <div class="d-flex mt-3">
                        <div class="spacer" />
                        <Zbutton color="primary" label="Tentar novamente" loading={starting} disabled={starting} on:click={startSigning} />
                    </div>
                {:else}
                    <div class="signers">
                        {#each signers as signer (signer.formV2FormSignatureId)}
                            <div class="signer_row">
                                <div class="signer_name">{signer.personName}</div>
                                {#if signer.personId !== form.personId}
                                    <Zbutton icon={faTrash} color="red" label="" on:click={() => removeSigner(signer.formV2FormSignatureId)} />
                                {/if}
                            </div>
                        {/each}
                        {#if signers.length === 0}
                            <div class="text-muted">Nenhuma pessoa adicionada ainda.</div>
                        {/if}
                    </div>

                    <div class="d-flex mt-3 align-items-end">
                        <ZentitySelect fonte="pessoa" class="flex-grow-1" label="Adicionar pessoa (opcional)" bind:value={newPerson} />
                        <Zbutton class="ms-2" color="secondary" label="Adicionar" disabled={!newPerson?.personId} on:click={addSigner} />
                    </div>

                    <Zinput class="mt-3" type="select" label="Modalidade" options={modeOptions} bind:value={mode} />

                    <Zbutton
                        class="mt-3"
                        color="primary"
                        icon={faSignature}
                        label="Iniciar assinatura"
                        loading={starting}
                        disabled={starting || signers.length === 0}
                        on:click={startSigning}
                    />
                {/if}
            {:else if form.status === SIGNING}
                <div class="alert alert-info">Assinatura em andamento. Os assinantes pendentes ainda não concluíram a assinatura.</div>

                <div class="signers">
                    {#each signers as signer (signer.formV2FormSignatureId)}
                        <div class="signer_block">
                            <div class="signer_row">
                                <div class="signer_name">{signer.personName}</div>
                                <div class="signer_status_group">
                                    <div class="signer_status">{signerStatusLabel(signer)}</div>
                                    {#if form.signatureMode === MODE_EMAIL && !signer.signedAt}
                                        <Zbutton
                                            color="secondary"
                                            icon={faEnvelope}
                                            label="Reenviar e-mail"
                                            loading={resendingId === signer.formV2FormSignatureId}
                                            disabled={!!resendingId}
                                            on:click={() => resendEmail(signer.formV2FormSignatureId)}
                                        />
                                    {/if}
                                </div>
                            </div>
                            {#if form.signatureMode === MODE_LINK && !signer.signedAt}
                                <div class="signer_link">
                                    <div class="signer_link_field">
                                        <Zinput disabled={true} label="Link de assinatura" value={buildSignLink(signer)} showCopy={true} />
                                    </div>
                                    <Zbutton icon={faPaperPlane} color="secondary" label="Enviar" on:click={() => sendSignLink(signer)} />
                                </div>
                            {/if}
                        </div>
                    {/each}
                </div>

                <div class="d-flex mt-3">
                    <Zbutton color="secondary" icon={faDownload} label="Baixar Documento" disabled={!form.signedDocumentUrl} on:click={downloadPdf} />
                    <div class="spacer" />
                    <Zbutton color="red" icon={faBan} label="Interromper assinatura" on:click={stopSigning} />
                </div>
            {:else if form.status === SIGNED}
                <div class="alert alert-success">
                    Documento assinado em {form.signedAt ? dayjs(form.signedAt).format('DD/MM/YYYY HH:mm') : '—'}.
                </div>

                <div class="signers">
                    {#each signers as signer (signer.formV2FormSignatureId)}
                        <div class="signer_row">
                            <div class="signer_name">{signer.personName}</div>
                            <div class="signer_status">{signerStatusLabel(signer)}</div>
                        </div>
                    {/each}
                </div>

                <div class="d-flex mt-3">
                    <Zbutton color="secondary" icon={faDownload} label="Baixar Documento" disabled={!form.signedDocumentUrl} on:click={downloadPdf} />
                    <Zbutton
                        class="ms-2"
                        color="secondary"
                        icon={faDownload}
                        label="Baixar Relatório"
                        disabled={!form.signatureReportUrl}
                        on:click={downloadReport}
                    />
                    <div class="spacer" />
                    <Zbutton color="secondary" icon={faClockRotateLeft} label="Ver histórico de assinaturas" on:click={() => (showLog = true)} />
                </div>
            {/if}
        {/if}
    </Zframe>
    <LoadSpinner show={show || starting}>{starting ? 'Gerando PDF, aguarde alguns instantes...' : ''}</LoadSpinner>
</div>

{#if showLog}
    <Popover title="Histórico de assinaturas" width="980px" on:close={() => (showLog = false)}>
        <FormV2SignLogPopover {formV2FormId} />
    </Popover>
{/if}

<style>
    .root {
        position: relative;
        min-height: 200px;
    }
    .signer_row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 0px;
    }
    .signer_block {
        border-bottom: 1px solid #99999930;
        padding-bottom: 6px;
        margin-bottom: 6px;
    }
    .signer_status_group {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .signer_status {
        opacity: 0.8;
    }
    .signer_missing {
        font-size: 0.8rem;
        color: #e8590c;
    }
    .signer_link {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        padding: 4px 0px 8px 0px;
    }
    .signer_link_field {
        flex: 1;
        min-width: 0;
    }
</style>
